/**
 * Endpunkte für .eml-Import, Ablage in der E-Akte und die zugehörige
 * Organisationseinstellung (Kundenfeedback 02.10.2026, IV). Seit dem
 * Kundenfeedback 18.10.2026 (XX): Vorschau vor dem Import, Ablage einzelner
 * Anhänge in Ordner der Akte (auch nachträglich) und die tatsächlichen
 * Ordner der Akte samt Vorlagen.
 */
import type { ImportResponse, PreviewFileResult } from './emailImport'
import { API_BASE_URL } from '@/config'
import { useAuthStore } from '@/stores/authStore'
import { findTarget, type FolderTargets, normalizeFolderTargets } from './caseFolders'

async function request (url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, { credentials: 'include', ...init })
  if (res.status === 401) {
    useAuthStore().logout()
    throw new Error('Sitzung abgelaufen')
  }
  return res
}

/** Je Datei (Upload-Reihenfolge) die abzulegenden Anhänge: Index → Kategorie. */
export type FilingPlan = Array<Array<{ index: number, category: string }>>

export type ImportTarget = { caseId: number, filing?: FilingPlan } | { orgId: number }

async function errorMessage (res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => null) as { message?: string } | null
  if (res.status === 403) {
    return 'Kein Zugriff auf diese Akte'
  }
  return body?.message || fallback
}

/**
 * Importiert E-Mail-Dateien in die Akte (mit Ablageplan für Anhänge) oder
 * ohne Akte (Zuordnung wie beim Webhook). 409/415/400 liefern dieselbe
 * Ergebnisliste wie 200 – die Einzelergebnisse erklären, was mit jeder
 * Datei geschah.
 */
export async function importEmailFiles (target: ImportTarget, files: File[]): Promise<ImportResponse> {
  const fd = new FormData()
  for (const file of files) {
    fd.append('files', file, file.name)
  }
  let url: string
  if ('caseId' in target) {
    url = `${API_BASE_URL}/protected/cases/${target.caseId}/import-email`
    if (target.filing) {
      fd.append('filing', JSON.stringify(target.filing))
    }
  } else {
    url = `${API_BASE_URL}/protected/organizations/${target.orgId}/inbox/import-email`
  }
  const res = await request(url, { method: 'POST', body: fd })
  const body = await res.clone().json().catch(() => null) as ImportResponse | null
  if (body && Array.isArray(body.results)) {
    return body
  }
  throw new Error(await errorMessage(res, 'Import fehlgeschlagen'))
}

/** Liest die Dateien vor dem Import: Betreff, Absender, echte Anhänge (XX). */
export async function previewEmailFiles (caseId: number, files: File[]): Promise<PreviewFileResult[]> {
  const fd = new FormData()
  for (const file of files) {
    fd.append('files', file, file.name)
  }
  const res = await request(`${API_BASE_URL}/protected/cases/${caseId}/import-email/preview`, { method: 'POST', body: fd })
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'E-Mail-Dateien konnten nicht gelesen werden'))
  }
  const body = await res.json().catch(() => null) as { results?: PreviewFileResult[] } | null
  return Array.isArray(body?.results) ? body.results : []
}

export interface FiledAttachment { idAttachment: number, idDocument: number, category: string }

/** Anlagen einer gespeicherten E-Mail einzeln in Ordner der Akte ablegen (XX, Nr. 5). */
export async function fileMessageAttachments (messageId: number, items: Array<{ idAttachment: number, category: string }>): Promise<FiledAttachment[]> {
  const res = await request(`${API_BASE_URL}/protected/messages/${messageId}/attachments/file-to-case`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  })
  if (!res.ok) {
    throw new Error(res.status === 409 ? 'Zuerst einer Akte zuordnen' : await errorMessage(res, 'Ablage fehlgeschlagen'))
  }
  const body = await res.json().catch(() => null) as { documents?: FiledAttachment[] } | null
  return Array.isArray(body?.documents) ? body.documents : []
}

/** Tatsächliche Ordner der Akte und Vorlagen (Endpunkt des Dokumentenbereichs). */
export async function loadFolderTargets (caseId: number): Promise<FolderTargets> {
  const res = await request(`${API_BASE_URL}/protected/cases/${caseId}/doc-folders/targets`)
  if (!res.ok) {
    throw new Error('Ordner der Akte konnten nicht geladen werden')
  }
  return normalizeFolderTargets(await res.json().catch(() => null))
}

async function postJSON (url: string, body: unknown): Promise<Response> {
  return request(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
}

/**
 * Macht aus einem gewählten Wert die Kategorie zum Ablegen: Vorlagen werden
 * dabei in der Akte angelegt (vordefiniert: Ordner einblenden; eigene
 * Vorlage: Ordner anlegen → "folder:<id>"). Ordner der Akte bleiben, wie sie sind.
 */
export async function resolveFolderValue (caseId: number, targets: FolderTargets, value: string): Promise<string> {
  const target = findTarget(targets, value)
  if (!target || (!target.classId && !target.templateId)) {
    return value
  }
  if (target.classId) {
    const res = await postJSON(`${API_BASE_URL}/protected/cases/${caseId}/doc-classes`, { classId: target.classId })
    if (!res.ok && res.status !== 409) {
      throw new Error(await errorMessage(res, `Ordner „${target.label}“ konnte nicht angelegt werden`))
    }
    return target.category
  }
  const res = await postJSON(`${API_BASE_URL}/protected/cases/${caseId}/doc-folders/from-template/${target.templateId}`, {})
  if (res.ok) {
    const folder = await res.json().catch(() => null) as { id?: number, data?: { id?: number } } | null
    const id = folder?.id ?? folder?.data?.id
    if (id) return `folder:${id}`
  }
  if (res.status === 409 || res.ok) {
    // Inzwischen angelegt: unter gleichem Namen in den Ordnern der Akte suchen.
    const fresh = await loadFolderTargets(caseId)
    const existing = fresh.folders.find(f => f.label === target.label)
    if (existing) return existing.category
  }
  throw new Error(await errorMessage(res, `Ordner „${target.label}“ konnte nicht angelegt werden`))
}

/** Alle gewählten Werte auflösen (jede Vorlage nur einmal anlegen). */
export async function resolveFolderValues (caseId: number, targets: FolderTargets, values: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>()
  for (const value of new Set(values)) {
    out.set(value, await resolveFolderValue(caseId, targets, value))
  }
  return out
}

export interface FiledMessageResult {
  idCase: number
  idDocument: number
  category: string
  title: string
  filename: string
  alreadyFiled: boolean
  attachmentDocumentIds: number[]
}

/** Nachricht als PDF (plus Anhänge) in der E-Akte ablegen. */
export async function fileMessageToCase (messageId: number, category: string, withAttachments: boolean): Promise<FiledMessageResult> {
  const res = await request(`${API_BASE_URL}/protected/messages/${messageId}/file-to-case`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ category, withAttachments }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { message?: string } | null
    if (res.status === 409) {
      throw new Error(body?.message || 'Zuerst einer Akte zuordnen')
    }
    if (res.status === 403) {
      throw new Error('Kein Zugriff auf diese Nachricht')
    }
    throw new Error(body?.message || 'Ablage fehlgeschlagen')
  }
  return res.json()
}

export interface EmailFilingSettings {
  autoFileSentEmails: boolean
  canEdit: boolean
}

export async function loadEmailFilingSettings (orgId: number): Promise<EmailFilingSettings> {
  const res = await request(`${API_BASE_URL}/protected/organizations/${orgId}/email-filing`)
  if (!res.ok) {
    throw new Error('Einstellung konnte nicht geladen werden')
  }
  return res.json()
}

export async function saveEmailFilingSettings (orgId: number, autoFileSentEmails: boolean): Promise<EmailFilingSettings> {
  const res = await request(`${API_BASE_URL}/protected/organizations/${orgId}/email-filing`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ autoFileSentEmails }),
  })
  if (!res.ok) {
    throw new Error(res.status === 403 ? 'Nur Administratoren und Unternehmensleiter dürfen die Ablage einstellen' : 'Einstellung konnte nicht gespeichert werden')
  }
  return res.json()
}
